import { createContext, type ReactNode, useContext } from "react"

import { ZH_PAGE_PATHS } from "../seo/prerender"
import { DEFAULT_LOCALE, type Locale, localizePath } from "./locale"
import { UI, type Ui } from "./ui"

const LocaleContext = createContext<Locale>(DEFAULT_LOCALE)

/** Sets the language of the page chrome below it; the brand layout derives it from the URL. */
export const LocaleProvider = ({ locale, children }: { locale: Locale; children: ReactNode }) => (
  <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>
)

export const useLocale = (): Locale => useContext(LocaleContext)

export const useUi = (): Ui => UI[useLocale()]

/**
 * Where an internal link goes on a page in `locale`: the Chinese version when the target has one,
 * otherwise the English page (fallback). External links, `#anchors`, `mailto:` and app routes are
 * left alone.
 */
export function localizeHref(href: string, locale: Locale): string {
  if (locale === DEFAULT_LOCALE || !href.startsWith("/") || href.startsWith("//")) return href
  const rest = /[#?]/.exec(href)
  const path = rest ? href.slice(0, rest.index) : href
  const tail = rest ? href.slice(rest.index) : ""
  const english = path.replace(/^\/zh(?=\/|$)/, "") || "/"
  if (!ZH_PAGE_PATHS.includes(english)) return href
  return `${localizePath(english, locale)}${tail}`
}

export const useLocalHref = (): ((href: string) => string) => {
  const locale = useLocale()
  return (href) => localizeHref(href, locale)
}
